import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  bulkUpdateFormDataWithRules,
  setUserMadeInteraction,
  updateControlValueAndApplyControlRules,
} from './actions/runtime.actions';
import {
  FORM_MODEL_ATTRIBUTES,
  READ_ONLY_TYPES,
  SS_CONTROL_TYPE,
  VIEW_ONLY_DATA_SOURCE_CONTROLS,
  ZOOM_TYPES,
} from './constants/form-manager.constants';
import { SSModalContext } from './context';
import ReadOnlyInputControl from './controls/ReadOnlyInputControl';
import getInput from './getInput';
import { hasVisibleWarning } from './selectors/common.selectors';
import { getEvaluatedConditions } from './selectors/form-manager.selectors';
import { getDataByController, getFieldLength } from './selectors/runtime.selectors';
import { getIsDynamicInputDisabled } from './utils/runtime.utils';
import { useFormSelector } from '../custom-hooks';
import { getUITA, isTrue } from '../utils/commonUtils';

const INPUT_TYPES = [
  SS_CONTROL_TYPE.TEXT_VIEW,
  SS_CONTROL_TYPE.TEXT_AREA,
  SS_CONTROL_TYPE.TEXT_BOX,
  SS_CONTROL_TYPE.DATE_BOX,
  SS_CONTROL_TYPE.NUMERIC_TEXT_BOX,
  SS_CONTROL_TYPE.CHECK_BOX,
  SS_CONTROL_TYPE.FILE_INPUT,
  SS_CONTROL_TYPE.SCORM_FILE_INPUT,
  SS_CONTROL_TYPE.SLIDER,
  SS_CONTROL_TYPE.AI_SUMMARY,
  ...ZOOM_TYPES,
];

const DynamicInput = ({
  id,
  index,
  entityName,
  fieldName,
  label,
  controlType,
  isDetail,
  isEditor,
  formKey,
  invokeFormRules,
  interaction,
  isViewOnly,
  instanceId,
  supportRichText,
  isInHeader,
  ...rest
}) => {
  const dispatch = useDispatch();
  const isRequired = useFormSelector(getEvaluatedConditions, {
    id,
    index,
    entityName,
    prop: FORM_MODEL_ATTRIBUTES.REQUIRED,
  });
  // get component value from formData
  const value = useFormSelector(getDataByController, { entityName, fieldName, index });
  const hasVisibleWarnings = useSelector(hasVisibleWarning(instanceId));
  const maxLength = useFormSelector(getFieldLength, { entityName, fieldName });
  const { mappingValues } = useContext(SSModalContext);

  // declare common actions
  const actions = {
    updateControlValue: useCallback(
      (newValue) => {
        if (newValue !== value) {
          dispatch(setUserMadeInteraction(formKey));
          dispatch(
            updateControlValueAndApplyControlRules(newValue, {
              entityName,
              fieldName,
              index,
              formKey,
              id,
              invokeFormRules: isTrue(invokeFormRules),
            })
          );
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dispatch, value, instanceId]
    ),
    bulkUpdateControlValues: useCallback(
      (changesArray) => dispatch(bulkUpdateFormDataWithRules(changesArray, formKey)),
      [dispatch, formKey]
    ),
  };

  // declare the component props
  const props = {
    ...rest,
    entityName,
    fieldName,
    id,
    index,
    label,
    isRequired,
    error: hasVisibleWarnings,
    isViewOnly,
    actions,
    isDetail,
    value,
    maxLength,
    formKey,
    isEditor,
    instanceId,
    controlType,
    supportRichText,
    disabled: getIsDynamicInputDisabled(mappingValues, entityName, fieldName),
    isInHeader,
  };

  if (isTrue(supportRichText)) {
    //rich text editor is a special case and is handled by the control itself
    return getInput(SS_CONTROL_TYPE.TEXT_AREA, props);
  }

  const isReadOnlyInputControl =
    (isDetail && INPUT_TYPES.includes(controlType)) ||
    ((isViewOnly || isDetail) &&
      !READ_ONLY_TYPES.includes(controlType) &&
      !VIEW_ONLY_DATA_SOURCE_CONTROLS.includes(controlType));

  if (isReadOnlyInputControl) {
    return (
      <ReadOnlyInputControl
        {...props}
        uita={getUITA(controlType.replace('ss', 'SS'), entityName, fieldName)}
      />
    );
  }

  return getInput(controlType, props);
};

export default React.memo(DynamicInput);
